import { Box, Button, Container, Typography, Avatar, Stack, Chip, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, Grid, IconButton, InputAdornment, TextField, CircularProgress, Card, CardContent, Alert, Collapse, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { userStore } from '../../app/stores/User';
import { navigationStore } from '../../app/stores/Navigation';
import { useStoreConfig } from '../../contexts/StoreConfigContext';
import { appConfigStore } from '../../app/stores/AppConfig';
import AuthService from '../../services/supabase/AuthService';
import OrdersService from '../../services/supabase/OrdersService';
import { useScreenComponent } from '../../app/hooks/custom/useScreenComponent';
import { ScreenComponent } from '../../models/enums/ScreenComponents';
import { AdminPanelSettings, Logout, Email, Phone, CalendarToday, Store as StoreIcon, Badge, ExitToApp, Visibility, VisibilityOff, CheckCircle, Store, ArrowForward, ArrowBack, ShoppingBag, ExpandMore, ExpandLess, Cancel as CancelIcon, Undo } from '@mui/icons-material';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTracking } from '../../app/hooks/custom/useTracking';
import { usePermissions } from '../../app/hooks/custom/usePermissions';
import { QuickActionCard } from '../../components/QuickActionCard';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getStoragePrefix } from '../../utils/storagePrefix';
import { ScrollView, Image } from 'react-native';
import { BasicButton } from '../../components/BasicButton';
import { BasicTextField } from '../../components/BasicTextField';
import { BasicPasswordField } from '../../components/BasicPasswordField';
import { stateErrorColor, stateSuccessColor } from '../../config/constants';
import { OrderDetails } from '../../models/DB';

interface ExistingStore {
	storeId: string;
	storeName: string;
	storeSlug: string;
	storeLogo: string;
	userFirstName: string;
	userLastName: string;
	registeredAt: string;
}

type AuthView = 'login' | 'register' | 'existingUser';

export const Profile = () => {
	const { trackPageView } = useTracking();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { user } = userStore((state) => state);
	const { effectivePermissions } = usePermissions('Profile');
	const { updateProfileDrawerOpen } = navigationStore((state) => state);
	const { config } = useStoreConfig();
	const { darkMode } = appConfigStore((state) => state);
	const { signOut, leaveStore, signInWithEmailAndPassword, checkExistingUser, createStandardUser, addStoreAccess } = AuthService();
	const { getMyOrders, cancelOrder, requestRefund } = OrdersService();
	const { navigateToScreen } = useScreenComponent();
	const [showLeaveDialog, setShowLeaveDialog] = useState(false);
	const [isLeavingStore, setIsLeavingStore] = useState(false);

	// Order history state
	const [orders, setOrders] = useState<OrderDetails[]>([]);
	const [ordersLoading, setOrdersLoading] = useState(false);
	const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
	const [refundDialogOpen, setRefundDialogOpen] = useState(false);
	const [refundOrderId, setRefundOrderId] = useState<string | null>(null);
	const [refundReason, setRefundReason] = useState('');
	const [refundNote, setRefundNote] = useState('');
	const [refundLoading, setRefundLoading] = useState(false);
	const [refundMessage, setRefundMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

	// Auth state for unauthenticated users
	const [authView, setAuthView] = useState<AuthView>('login');
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [existingUserEmail, setExistingUserEmail] = useState<string>('');
	const [existingStores, setExistingStores] = useState<ExistingStore[]>([]);
	const [existingUserPassword, setExistingUserPassword] = useState('');
	const [showExistingPassword, setShowExistingPassword] = useState(false);
	const [existingUserSource, setExistingUserSource] = useState<'login' | 'signup'>('signup');

	// Transition state - keeps auth form visible during transition to prevent flicker
	const [isTransitioning, setIsTransitioning] = useState(false);

	// Memoize the expensive portal access check to prevent re-computation on every render
	const hasPortalAccessForStore = useMemo(() => {
		return effectivePermissions?.allowedScreens && effectivePermissions.allowedScreens.length > 0;
	}, [effectivePermissions?.allowedScreens]);

	const primaryColor = config.primaryColor || '#800080';
	const { info, role, authenticated } = user;
	
	// Background color for auth screens - darker in dark mode
	const authBackgroundColor = darkMode ? '#121212' : theme.palette.background.default;

	// Check if this is NOT the user's primary store
	const storeIds: string[] = (info?.app_metadata?.store_ids as string[]) || [];
	const currentStoreId = config.storeId;
	const isPrimaryStore = storeIds.length > 0 && storeIds[0] === currentStoreId;
	const canLeaveStore = !isPrimaryStore && storeIds.includes(currentStoreId || '');

	const containerPadding = 42;

	useEffect(() => {
		trackPageView('Profile');
	}, []);

	// Reset transition state when user signs out
	useEffect(() => {
		if (!authenticated && isTransitioning) {
			// Small delay to let the profile fade out first
			const timer = setTimeout(() => {
				setIsTransitioning(false);
				setLoading(false);
			}, 100);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [authenticated, isTransitioning]);

	const fetchOrders = useCallback(async () => {
		if (!authenticated) return;
		setOrdersLoading(true);
		try {
			const data = await getMyOrders();
			setOrders(data);
		} catch (e) {
			console.error('Failed to fetch orders', e);
		} finally {
			setOrdersLoading(false);
		}
	}, [authenticated]);

	useEffect(() => {
		if (authenticated) {
			fetchOrders();
		}
	}, [authenticated]);

	const handleCancelOrder = async (orderId: string) => {
		const success = await cancelOrder(orderId);
		if (success) {
			fetchOrders();
		}
	};

	const handleRefundSubmit = async () => {
		if (!refundOrderId || !refundReason) return;
		setRefundLoading(true);
		setRefundMessage(null);
		const result = await requestRefund(refundOrderId, refundReason, refundNote || undefined);
		setRefundLoading(false);
		if (result.success) {
			setRefundMessage({ type: 'success', text: 'Refund request submitted successfully' });
			fetchOrders();
			setTimeout(() => {
				setRefundDialogOpen(false);
				setRefundMessage(null);
				setRefundReason('');
				setRefundNote('');
			}, 1500);
		} else {
			setRefundMessage({ type: 'error', text: result.message || 'Failed to submit refund' });
		}
	};

	const getStatusLabel = (status: number) => {
		const map: Record<number, { label: string; color: string }> = {
			1: { label: 'Pending', color: '#ff9800' },
			2: { label: 'Processing', color: '#2196f3' },
			3: { label: 'Completed', color: '#4caf50' },
			4: { label: 'Cancelled', color: '#9e9e9e' },
			5: { label: 'Refund Requested', color: '#ff5722' },
			6: { label: 'Refund Approved', color: '#e91e63' },
			7: { label: 'Refunded', color: '#673ab7' },
			8: { label: 'Refund Rejected', color: '#f44336' },
		};
		return map[status] || { label: 'Unknown', color: '#9e9e9e' };
	};

	const handleSignOut = async () => {
		await signOut();
	};

	const handlePortal = () => {
		updateProfileDrawerOpen(false);
		navigateToScreen(ScreenComponent.Portal);
	};

	const handleLeaveStore = async () => {
		setIsLeavingStore(true);
		const result = await leaveStore();
		setIsLeavingStore(false);
		
		if (result.success) {
			// Close dialog and drawer
			setShowLeaveDialog(false);
			updateProfileDrawerOpen(false);
			// Sign them out so they can choose which store to access
			await signOut();
		} else {
			// Show error (could use a toast/snackbar here)
			alert(result.message);
		}
	};

	const handleClearCache = async () => {
		try {
			const prefix = getStoragePrefix();
			const allKeys = await AsyncStorage.getAllKeys();
			const storeKeys = allKeys.filter(key => key.startsWith(prefix));
			
			if (storeKeys.length > 0) {
				await AsyncStorage.multiRemove(storeKeys);
				console.log(`🗑️ Cleared ${storeKeys.length} cached items for store`);
			}
			
			// Close the profile drawer and reload the page
			updateProfileDrawerOpen(false);
			if (typeof window !== 'undefined') {
				window.location.reload();
			}
		} catch (error) {
			console.error('Failed to clear cache:', error);
		}
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return 'N/A';
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};

	const getInitials = () => {
		const firstName = info?.user_metadata?.firstName || '';
		const lastName = info?.user_metadata?.lastName || '';
		return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
	};

	const getRoleColor = () => {
		switch (role?.toLowerCase()) {
			case 'admin':
				return theme.palette.error.main;
			case 'moderator':
				return theme.palette.warning.main;
			default:
				return theme.palette.info.main;
		}
	};

	// Store Logo Component
	const StoreLogo = ({ size = 140 }: { size?: number }) => (
		<Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
			{config.appLogo ? (
				<Avatar
					src={config.appLogo}
					alt={config.companyName}
					sx={{
						width: size,
						height: size,
						border: `2px solid ${theme.palette.divider}`,
						backgroundColor: theme.palette.background.paper
					}}
				>
					{config.companyName?.charAt(0) || 'S'}
				</Avatar>
			) : (
				<Image
					source={darkMode ? require('../../assets/icon-white-bg.svg') : require('../../assets/icon-black-bg.svg')}
					style={{
						width: size,
						height: size,
						resizeMode: 'contain',
					}}
				/>
			)}
		</Box>
	);

	// Login handler
	const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setLoading(true);
		setErrorMessage(null);
		const data = new FormData(event.currentTarget);
		const email = data.get('email') as string;
		const password = data.get('password') as string;

		const { success, message } = await signInWithEmailAndPassword(email, password);

		if (success) {
			// Keep loading visible during transition - don't update any other state
			// The authenticated state from userStore will trigger the profile view
			setIsTransitioning(true);
			return; // Don't set loading to false - let it stay on during transition
		} else if (message && message.includes('NO_STORE_ACCESS')) {
			// User exists but doesn't have access to this store
			const { exists, stores } = await checkExistingUser(email);
			if (exists) {
				setExistingUserEmail(email);
				setExistingStores(stores || []);
				setExistingUserSource('login');
				setAuthView('existingUser');
				setLoading(false);
				return;
			}
			setLoading(false);
		} else {
			setErrorMessage(message);
			setLoading(false);
		}
	};

	// Register handler
	const handleRegister = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setLoading(true);
		setErrorMessage(null);
		setSuccessMessage(null);
		const data = new FormData(event.currentTarget);
		const firstName = data.get('firstName') as string;
		const lastName = data.get('lastName') as string;
		const email = data.get('email') as string;
		const mobileNumber = data.get('mobileNumber') as string;
		const password = data.get('password') as string;

		const result = await createStandardUser(
			email,
			password,
			mobileNumber,
			firstName,
			lastName
		);
		setLoading(false);

		// Check if user already exists on platform
		if (result.existingUser && result.existingStores) {
			setExistingUserEmail(email);
			setExistingStores(result.existingStores);
			setExistingUserSource('signup');
			setAuthView('existingUser');
			return;
		}

		if (result.success) {
			setSuccessMessage(result.message);
		} else {
			setErrorMessage(result.message);
		}
	};

	// Existing user add store handler
	const handleAddStore = async () => {
		if (!existingUserPassword) {
			setErrorMessage('Please enter your password');
			return;
		}

		setLoading(true);
		setErrorMessage(null);

		const result = await addStoreAccess(existingUserEmail, existingUserPassword);

		if (result.success && result.user && result.session) {
			// Keep loading visible during transition
			setIsTransitioning(true);
			return; // Don't set loading to false - let it stay on during transition
		} else {
			setErrorMessage(result.message || 'Incorrect password. Please try again.');
			setLoading(false);
		}
	};

	// Login Form Component
	const LoginForm = () => (
		<Box
			sx={{
				flex: 1,
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				backgroundColor: authBackgroundColor
			}}
		>
			<ScrollView
				style={{ flex: 1 }}
				contentContainerStyle={{
					flexGrow: 1,
					padding: containerPadding,
					justifyContent: 'center',
				}}
				showsVerticalScrollIndicator={false}
			>
				{/* Store Logo */}
				<StoreLogo />

				<Typography
					variant='h5'
					align='center'
					sx={{
						mb: 0.5,
						fontWeight: 600,
						color: theme.palette.text.primary
					}}
				>
					Sign In
				</Typography>
				<Typography
					variant='body2'
					align='center'
					sx={{
						mb: 4,
						color: theme.palette.text.secondary
					}}
				>
					{config.companyName ? `Welcome to ${config.companyName}` : 'Your personalised experience'}
				</Typography>

				<Box
					component='form'
					onSubmit={handleLogin}
					sx={{ width: '100%' }}
				>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<BasicTextField
								fullWidth
								id='email'
								name='email'
								label='Email'
								InputProps={{ style: { color: theme.palette.text.primary } }}
							/>
						</Grid>
						<Grid item xs={12}>
							<BasicPasswordField
								fullWidth
								id='password'
								name='password'
								label='Password'
								InputProps={{ style: { color: theme.palette.text.primary } }}
							/>
						</Grid>
						{errorMessage && (
							<Grid item xs={12}>
								<Typography
									variant='body2'
									align='center'
									sx={{ color: stateErrorColor }}
								>
									{errorMessage}
								</Typography>
							</Grid>
						)}
					</Grid>

					<BasicButton
						title='Sign In'
						type='submit'
						style={{
							marginTop: 20,
							marginBottom: 16,
							width: '100%',
							backgroundColor: primaryColor
						}}
						isLoading={loading}
					/>

					<Box sx={{ textAlign: 'center' }}>
						<Typography
							variant='body2'
							sx={{
								display: 'inline',
								fontSize: 12,
								color: theme.palette.text.secondary
							}}
						>
							Don't have an account?{' '}
						</Typography>
						<Button
							variant='text'
							onClick={() => {
								setErrorMessage(null);
								setAuthView('register');
							}}
							disabled={loading}
							sx={{
								display: 'inline',
								padding: '0 4px',
								minWidth: 'auto',
								color: theme.palette.text.primary,
								fontWeight: 600,
								textTransform: 'none'
							}}
						>
							Create an account
						</Button>
					</Box>
				</Box>
			</ScrollView>
		</Box>
	);

	// Register Form Component
	const RegisterForm = () => (
		<Box
			sx={{
				flex: 1,
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				backgroundColor: authBackgroundColor
			}}
		>
			<ScrollView
				style={{ flex: 1 }}
				contentContainerStyle={{
					flexGrow: 1,
					padding: containerPadding,
					justifyContent: 'center',
				}}
				showsVerticalScrollIndicator={false}
			>
				{/* Store Logo */}
				<StoreLogo />

				<Typography
					variant='h5'
					align='center'
					sx={{
						mb: 0.5,
						fontWeight: 600,
						color: theme.palette.text.primary
					}}
				>
					Create an account
				</Typography>
				<Typography
					variant='body2'
					align='center'
					sx={{
						mb: 4,
						color: theme.palette.text.secondary
					}}
				>
					Join {config.companyName || 'our store'} today!
				</Typography>

				<Box
					component='form'
					onSubmit={handleRegister}
					sx={{ width: '100%' }}
				>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<BasicTextField
								fullWidth
								id='firstName'
								name='firstName'
								label='First Name'
								InputProps={{ style: { color: theme.palette.text.primary } }}
							/>
						</Grid>
						<Grid item xs={6}>
							<BasicTextField
								fullWidth
								id='lastName'
								name='lastName'
								label='Last Name'
								InputProps={{ style: { color: theme.palette.text.primary } }}
							/>
						</Grid>
						<Grid item xs={12}>
							<BasicTextField
								fullWidth
								id='email'
								name='email'
								label='Email'
								InputProps={{ style: { color: theme.palette.text.primary } }}
							/>
						</Grid>
						<Grid item xs={12}>
							<BasicTextField
								fullWidth
								id='mobileNumber'
								name='mobileNumber'
								label='Phone Number'
								InputProps={{ style: { color: theme.palette.text.primary } }}
							/>
						</Grid>
						<Grid item xs={12}>
							<BasicPasswordField
								fullWidth
								id='password'
								name='password'
								label='Password'
								InputProps={{ style: { color: theme.palette.text.primary } }}
							/>
						</Grid>
						{(errorMessage || successMessage) && (
							<Grid item xs={12}>
								<Typography
									variant='body2'
									align='center'
									sx={{ color: successMessage ? stateSuccessColor : stateErrorColor }}
								>
									{successMessage || errorMessage}
								</Typography>
							</Grid>
						)}
					</Grid>

					<BasicButton
						title='Create an account'
						type='submit'
						style={{
							marginTop: 20,
							marginBottom: 16,
							width: '100%',
							backgroundColor: primaryColor
						}}
						isLoading={loading}
					/>

					<Box sx={{ textAlign: 'center' }}>
						<Typography
							variant='body2'
							sx={{
								display: 'inline',
								fontSize: 12,
								color: theme.palette.text.secondary
							}}
						>
							Already have an account?{' '}
						</Typography>
						<Button
							variant='text'
							onClick={() => {
								setErrorMessage(null);
								setSuccessMessage(null);
								setAuthView('login');
							}}
							disabled={loading}
							sx={{
								display: 'inline',
								padding: '0 4px',
								minWidth: 'auto',
								color: theme.palette.text.primary,
								fontWeight: 600,
								textTransform: 'none'
							}}
						>
							Sign in
						</Button>
					</Box>
				</Box>
			</ScrollView>
		</Box>
	);

	// Existing User Screen Component
	const ExistingUserContent = () => (
		<Box
			sx={{
				flex: 1,
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				backgroundColor: authBackgroundColor
			}}
		>
			<ScrollView
				style={{ flex: 1 }}
				contentContainerStyle={{
					flexGrow: 1,
					padding: containerPadding,
				}}
				showsVerticalScrollIndicator={false}
			>
				{/* Header */}
				<Box sx={{ textAlign: 'center', mb: 3 }}>
					<Box sx={{
						display: 'inline-flex',
						p: 2,
						borderRadius: '50%',
						background: `linear-gradient(135deg, ${primaryColor}20, ${primaryColor}40)`,
						mb: 2
					}}>
						{config.appLogo ? (
							<Avatar
								src={config.appLogo}
								alt={config.companyName}
								sx={{ width: 50, height: 50 }}
							>
								{config.companyName?.charAt(0) || 'S'}
							</Avatar>
						) : (
								<Image
									source={darkMode
										? require('../../assets/icon-white-bg.svg')
										: require('../../assets/icon-black-bg.svg')
									}
									style={{ width: 40, height: 40 }}
									resizeMode='contain'
								/>
						)}
					</Box>

					<Typography
						variant='h5'
						sx={{
							fontWeight: 700,
							color: theme.palette.text.primary,
							mb: 1
						}}
					>
						{existingUserSource === 'login' ? 'Almost There! 🎯' : 'Welcome Back! 👋'}
					</Typography>

					<Typography
						variant='body2'
						sx={{
							color: theme.palette.text.secondary,
							mb: 2
						}}
					>
						{existingUserSource === 'login'
							? 'You already have a Cosmo Cart account, but need access to this store'
							: 'You\'re already part of the Cosmo Cart platform'}
					</Typography>

					<Chip
						icon={<CheckCircle sx={{ fontSize: 16 }} />}
						label={existingUserEmail}
						sx={{
							backgroundColor: `${primaryColor}15`,
							color: primaryColor,
							fontWeight: 500,
							'& .MuiChip-icon': { color: primaryColor }
						}}
					/>
				</Box>

				{/* Info Card */}
				<Card sx={{
					mb: 3,
					backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: 3
				}}>
					<CardContent sx={{ p: 2 }}>
						<Typography variant='body2' sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
							{existingUserSource === 'login' ? (
								<>
									<strong style={{ color: theme.palette.text.primary }}>Verify your password to continue</strong>
									<br />
									Once verified, you'll be able to access this store with the same account.
								</>
							) : (
								<>
									<strong style={{ color: theme.palette.text.primary }}>No new account needed!</strong>
									<br />
									Use your existing password to join this store instantly.
								</>
							)}
						</Typography>
					</CardContent>
				</Card>

				{/* Existing Stores */}
				{existingStores.length > 0 && (
					<Box sx={{ mb: 3 }}>
						<Typography
							variant='overline'
							sx={{
								color: theme.palette.text.secondary,
								letterSpacing: 1.5,
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								mb: 1.5
							}}
						>
							<Store sx={{ fontSize: 16 }} />
							Your Current Stores
						</Typography>

						{existingStores.map((store) => (
							<Card
								key={store.storeId}
								sx={{
									mb: 1.5,
									backgroundColor: theme.palette.background.paper,
									border: `1px solid ${theme.palette.divider}`,
									borderRadius: 2
								}}
							>
								<CardContent sx={{ display: 'flex', alignItems: 'center', py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
									<Avatar
										src={store.storeLogo.startsWith('http') ? store.storeLogo : `https://bspxoqajkrjbgjqflykh.supabase.co/storage/v1/object/public/stores/${store.storeSlug}/${store.storeLogo}`}
										alt={store.storeName}
										sx={{
											width: 40,
											height: 40,
											mr: 2,
											border: `2px solid ${theme.palette.divider}`
										}}
									>
										{store.storeName.charAt(0)}
									</Avatar>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography variant='subtitle2' fontWeight={600} noWrap>
											{store.storeName}
										</Typography>
										<Typography variant='caption' color='text.secondary'>
											{store.userFirstName} {store.userLastName}
										</Typography>
									</Box>
									<CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
								</CardContent>
							</Card>
						))}
					</Box>
				)}

				{/* Join This Store */}
				<Card
					sx={{
						background: `linear-gradient(135deg, ${primaryColor}08, ${primaryColor}15)`,
						border: `2px solid ${primaryColor}`,
						borderRadius: 3,
						mb: 3
					}}
				>
					<CardContent sx={{ p: 2.5 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
							<Avatar
								src={config.appLogo}
								alt={config.companyName}
								sx={{
									width: 48,
									height: 48,
									mr: 2,
									backgroundColor: primaryColor,
									border: `3px solid ${theme.palette.background.paper}`
								}}
							>
								{config.companyName?.charAt(0) || 'S'}
							</Avatar>
							<Box sx={{ flex: 1 }}>
								<Typography variant='subtitle1' fontWeight={700}>
									{config.companyName || 'This Store'}
								</Typography>
								<Chip
									label='NEW STORE'
									size='small'
									sx={{
										height: 18,
										fontSize: 10,
										fontWeight: 700,
										backgroundColor: primaryColor,
										color: '#fff'
									}}
								/>
							</Box>
						</Box>

						{successMessage ? (
							<Box sx={{ textAlign: 'center', py: 2 }}>
								<CheckCircle sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
								<Typography variant='subtitle1' fontWeight={600} color='#4caf50'>
									Welcome! 🎉
								</Typography>
								<Typography variant='body2' color='text.secondary'>
									Taking you to the shop...
								</Typography>
							</Box>
						) : (
							<>
								<TextField
									fullWidth
									type={showExistingPassword ? 'text' : 'password'}
									label='Your Cosmo Cart Password'
									placeholder='Enter your existing password'
									value={existingUserPassword}
									onChange={(e) => setExistingUserPassword(e.target.value)}
									onKeyPress={(e) => e.key === 'Enter' && handleAddStore()}
									disabled={loading}
									InputProps={{
										endAdornment: (
											<InputAdornment position='end'>
												<IconButton
													onClick={() => setShowExistingPassword(!showExistingPassword)}
													edge='end'
													size='small'
												>
													{showExistingPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										)
									}}
									sx={{
										mb: 2,
										'& .MuiOutlinedInput-root': {
											backgroundColor: theme.palette.background.paper
										}
									}}
								/>

								{errorMessage && (
									<Alert severity='error' sx={{ mb: 2, borderRadius: 2 }}>
										{errorMessage}
									</Alert>
								)}

								<Button
									fullWidth
									variant='contained'
									size='large'
									onClick={handleAddStore}
									disabled={loading || !existingUserPassword}
									endIcon={!loading && <ArrowForward />}
									sx={{
										backgroundColor: primaryColor,
										py: 1.5,
										fontWeight: 600,
										borderRadius: 2,
										textTransform: 'none',
										'&:hover': {
											backgroundColor: primaryColor,
											filter: 'brightness(1.1)'
										}
									}}
								>
									{loading ? (
										<CircularProgress size={24} color='inherit' />
									) : (
										`Join ${config.companyName || 'This Store'}`
									)}
								</Button>
							</>
						)}
					</CardContent>
				</Card>

				{/* Back button */}
				<Box sx={{ textAlign: 'center' }}>
					<Button
						variant='text'
						startIcon={<ArrowBack />}
						onClick={() => {
							setAuthView('login');
							setErrorMessage(null);
							setExistingUserEmail('');
							setExistingStores([]);
							setExistingUserPassword('');
						}}
						disabled={loading}
						sx={{
							color: theme.palette.text.secondary,
							textTransform: 'none'
						}}
					>
						Use a different email
					</Button>
				</Box>
			</ScrollView>
		</Box>
	);

	// Authenticated Profile Content
	const ProfileContent = () => {
		return (
			<Box
				sx={{
					height: '100vh',
					display: 'flex',
					flexDirection: 'column',
					backgroundColor: theme.palette.background.default
				}}
			>
				{/* Header - Friendly Greeting */}
				<Box
					sx={{
						pt: { xs: 3, sm: 4 },
						pb: { xs: 2, sm: 3 },
						px: { xs: 2, sm: 3 },
						backgroundColor: theme.palette.background.default
					}}
				>
					<Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between'>
						<Stack>
							<Typography
								variant='h4'
								sx={{
									color: theme.palette.text.primary,
									fontWeight: 700,
									fontSize: { xs: '1.75rem', sm: '2rem' }
								}}
							>
								Hello, {info?.user_metadata?.firstName || 'there'}
							</Typography>
							<Typography
								variant='body2'
								sx={{
									color: theme.palette.text.secondary,
									fontSize: '0.95rem',
									mt: 0.5
								}}
							>
								{hasPortalAccessForStore
									? 'Manage your store and account'
									: `Welcome to ${config.companyName || 'Cosmo Cart'}`}
							</Typography>
						</Stack>
						<Avatar
							sx={{
								width: { xs: 50, sm: 60 },
								height: { xs: 50, sm: 60 },
								bgcolor: primaryColor,
								color: 'white',
								fontSize: { xs: '1.25rem', sm: '1.5rem' },
								fontWeight: 700,
								boxShadow: `0 4px 12px ${primaryColor}40`
							}}
							src={info?.user_metadata?.avatar_url}
						>
							{getInitials()}
						</Avatar>
					</Stack>
				</Box>

				{/* Main content */}
				<Box
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						minHeight: 0
					}}
				>
					<Container
						maxWidth='md'
						disableGutters={isMobile}
						sx={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							py: 3,
							px: { xs: 2, sm: 3 },
							minHeight: 0
						}}
					>
						{/* Scrollable Content Area */}
						<Box sx={{ flex: 1, overflow: 'visible', minHeight: 0 }}>
							{/* Quick Actions Section */}
							<Box sx={{ mb: 3 }}>
								<Typography
									variant='h6'
									sx={{
										fontWeight: 700,
										color: theme.palette.text.primary,
										mb: 2,
										fontSize: '1.1rem'
									}}
								>
									Quick Actions
								</Typography>
								<Grid container spacing={2}>
									{/* Admin Portal Card */}
									{hasPortalAccessForStore && (
										<Grid item xs={6}>
											<QuickActionCard
												icon={<AdminPanelSettings sx={{ fontSize: 24, color: primaryColor }} />}
												title='Admin Portal'
												subtitle='Manage your store'
												onClick={handlePortal}
												backgroundColor={primaryColor}
											/>
										</Grid>
									)}

									{/* Sign Out Card */}
									<Grid item xs={6}>
										<QuickActionCard
											icon={<Logout sx={{ fontSize: 24, color: theme.palette.error.main }} />}
											title='Sign Out'
											subtitle='End your session'
											onClick={handleSignOut}
											backgroundColor={theme.palette.error.main}
										/>
									</Grid>

									{/* Leave Store Card - Only show if not primary store */}
									{canLeaveStore && (
										<Grid item xs={6}>
											<QuickActionCard
												icon={<ExitToApp sx={{ fontSize: 24, color: theme.palette.warning.main }} />}
												title='Leave Store'
												subtitle='Switch to another store'
												onClick={() => setShowLeaveDialog(true)}
												backgroundColor={theme.palette.warning.main}
											/>
										</Grid>
									)}
								</Grid>
							</Box>

							{/* Account Information */}
							<Box sx={{ mb: 3 }}>
								<Typography
									variant='h6'
									sx={{
										fontWeight: 700,
										color: theme.palette.text.primary,
										mb: 2,
										fontSize: '1.1rem'
									}}
								>
									Account Information
								</Typography>
								<Box
									sx={{
										backgroundColor: theme.palette.background.paper,
										borderRadius: 3,
										overflow: 'hidden',
										border: `1px solid ${theme.palette.divider}`
									}}
								>
									<Stack spacing={0}>
										{/* Email */}
										<Box
											sx={{
												px: 2.5,
												py: 2,
												borderBottom: `1px solid ${theme.palette.divider}`,
												display: 'flex',
												alignItems: 'center',
												gap: 2
											}}
										>
											<Box
												sx={{
													width: 36,
													height: 36,
													borderRadius: 2,
													backgroundColor: `${primaryColor}15`,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center'
												}}
											>
												<Email sx={{ color: primaryColor, fontSize: 18 }} />
											</Box>
											<Box flex={1}>
												<Typography variant='caption' color='text.secondary' fontSize='0.7rem'>
													Email Address
												</Typography>
												<Typography variant='body2' fontWeight={600} fontSize='0.875rem'>
													{info?.email || 'N/A'}
												</Typography>
											</Box>
										</Box>

										{/* Mobile Number */}
										{info?.user_metadata?.mobileNumber && (
											<Box
												sx={{
													px: 2.5,
													py: 2,
													borderBottom: `1px solid ${theme.palette.divider}`,
													display: 'flex',
													alignItems: 'center',
													gap: 2
												}}
											>
												<Box
													sx={{
														width: 36,
														height: 36,
														borderRadius: 2,
														backgroundColor: `${theme.palette.success.main}15`,
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center'
													}}
												>
													<Phone sx={{ color: theme.palette.success.main, fontSize: 18 }} />
												</Box>
												<Box flex={1}>
													<Typography variant='caption' color='text.secondary' fontSize='0.7rem'>
														Mobile Number
													</Typography>
													<Typography variant='body2' fontWeight={600} fontSize='0.875rem'>
														{info.user_metadata.mobileNumber}
													</Typography>
												</Box>
											</Box>
										)}

										{/* Member Since */}
										<Box
											sx={{
												px: 2.5,
												py: 2,
												borderBottom: `1px solid ${theme.palette.divider}`,
												display: 'flex',
												alignItems: 'center',
												gap: 2
											}}
										>
											<Box
												sx={{
													width: 36,
													height: 36,
													borderRadius: 2,
													backgroundColor: `${theme.palette.info.main}15`,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center'
												}}
											>
												<CalendarToday sx={{ color: theme.palette.info.main, fontSize: 18 }} />
											</Box>
											<Box flex={1}>
												<Typography variant='caption' color='text.secondary' fontSize='0.7rem'>
													Member Since
												</Typography>
												<Typography variant='body2' fontWeight={600} fontSize='0.875rem'>
													{formatDate(info?.created_at)}
												</Typography>
											</Box>
										</Box>

										{/* Store */}
										<Box
											sx={{
												px: 2.5,
												py: 2,
												display: 'flex',
												alignItems: 'center',
												gap: 2
											}}
										>
											<Box
												sx={{
													width: 36,
													height: 36,
													borderRadius: 2,
													backgroundColor: `${theme.palette.secondary.main}15`,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center'
												}}
											>
												<StoreIcon sx={{ color: theme.palette.secondary.main, fontSize: 18 }} />
											</Box>
											<Box flex={1}>
												<Typography variant='caption' color='text.secondary' fontSize='0.7rem'>
													Current Store
												</Typography>
												<Typography variant='body2' fontWeight={600} fontSize='0.875rem'>
													{config.companyName || 'Cosmo Cart'}
												</Typography>
												{hasPortalAccessForStore && role && (
													<Chip
														icon={<Badge sx={{ fontSize: 12 }} />}
														label={role}
														size='small'
														sx={{
															mt: 0.5,
															height: 20,
															backgroundColor: getRoleColor(),
															color: 'white',
															fontWeight: 600,
															fontSize: '0.65rem',
															'& .MuiChip-icon': {
																color: 'white',
																fontSize: 12
															}
														}}
													/>
												)}
											</Box>
										</Box>
									</Stack>
								</Box>
							</Box>
						</Box>

						{/* My Orders Section */}
						<Box sx={{ mb: 3 }}>
							<Typography
								variant='h6'
								sx={{
									fontWeight: 700,
									color: theme.palette.text.primary,
									mb: 2,
									fontSize: '1.1rem',
									display: 'flex',
									alignItems: 'center',
									gap: 1
								}}
							>
								<ShoppingBag sx={{ fontSize: 20 }} />
								My Orders
							</Typography>

							{ordersLoading ? (
								<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
									<CircularProgress size={28} sx={{ color: primaryColor }} />
								</Box>
							) : orders.length === 0 ? (
								<Box
									sx={{
										backgroundColor: theme.palette.background.paper,
										borderRadius: 3,
										border: `1px solid ${theme.palette.divider}`,
										p: 3,
										textAlign: 'center'
									}}
								>
									<ShoppingBag sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1 }} />
									<Typography variant='body2' color='text.secondary'>
										No orders yet
									</Typography>
								</Box>
							) : (
								<Stack spacing={1.5}>
									{orders.map((order) => {
										const statusInfo = getStatusLabel(order.status || 1);
										const isExpanded = expandedOrderId === order.id;
										const items = order.order || [];
										const canCancel = order.status === 1;
										const canRefund = order.status === 2 || order.status === 3;

										return (
											<Box
												key={order.id}
												sx={{
													backgroundColor: theme.palette.background.paper,
													borderRadius: 3,
													border: `1px solid ${theme.palette.divider}`,
													overflow: 'hidden'
												}}
											>
												<Box
													onClick={() => setExpandedOrderId(isExpanded ? null : order.id || null)}
													sx={{
														px: 2.5,
														py: 2,
														cursor: 'pointer',
														display: 'flex',
														alignItems: 'center',
														gap: 1.5,
														'&:hover': { backgroundColor: theme.palette.action.hover }
													}}
												>
													<Box sx={{ flex: 1, minWidth: 0 }}>
														<Typography variant='body2' fontWeight={600} noWrap>
															#{order.orderId}
														</Typography>
														<Typography variant='caption' color='text.secondary'>
															{formatDate(order.created as any)}
														</Typography>
													</Box>
													<Chip
														label={statusInfo.label}
														size='small'
														sx={{
															backgroundColor: `${statusInfo.color}20`,
															color: statusInfo.color,
															fontWeight: 600,
															fontSize: '0.7rem',
															height: 24
														}}
													/>
													<Typography variant='body2' fontWeight={700}>
														R{(order.totalCost || 0).toFixed(2)}
													</Typography>
													{isExpanded ? <ExpandLess /> : <ExpandMore />}
												</Box>

												<Collapse in={isExpanded}>
													<Box sx={{ px: 2.5, pb: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
														{items.length > 0 && (
															<Box sx={{ pt: 1.5, mb: 1.5 }}>
																{items.map((item: any, idx: number) => (
																	<Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
																		<Typography variant='caption' color='text.secondary'>
																			{item.name || item.code} x{item.quantity || 1}
																		</Typography>
																		<Typography variant='caption' fontWeight={600}>
																			R{((item.price || 0) * (item.quantity || 1)).toFixed(2)}
																		</Typography>
																	</Box>
																))}
															</Box>
														)}
														<Stack direction='row' spacing={1} sx={{ mt: 1 }}>
															{canCancel && (
																<Button
																	size='small'
																	variant='outlined'
																	color='error'
																	startIcon={<CancelIcon sx={{ fontSize: 14 }} />}
																	onClick={() => handleCancelOrder(order.id || '')}
																	sx={{ textTransform: 'none', fontSize: '0.75rem' }}
																>
																	Cancel Order
																</Button>
															)}
															{canRefund && (
																<Button
																	size='small'
																	variant='outlined'
																	startIcon={<Undo sx={{ fontSize: 14 }} />}
																	onClick={() => {
																		setRefundOrderId(order.id || null);
																		setRefundDialogOpen(true);
																	}}
																	sx={{ textTransform: 'none', fontSize: '0.75rem', borderColor: primaryColor, color: primaryColor }}
																>
																	Request Refund
																</Button>
															)}
														</Stack>
													</Box>
												</Collapse>
											</Box>
										);
									})}
								</Stack>
							)}
						</Box>

						{/* Refund Request Dialog */}
						<Dialog
							open={refundDialogOpen}
							onClose={() => !refundLoading && setRefundDialogOpen(false)}
							maxWidth='sm'
							fullWidth
						>
							<DialogTitle>Request a Refund</DialogTitle>
							<DialogContent>
								<FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
									<InputLabel>Reason</InputLabel>
									<Select
										value={refundReason}
										label='Reason'
										onChange={(e) => setRefundReason(e.target.value)}
									>
										<MenuItem value='defective'>Defective / Damaged</MenuItem>
										<MenuItem value='wrong_item'>Wrong Item Received</MenuItem>
										<MenuItem value='not_as_described'>Not as Described</MenuItem>
										<MenuItem value='not_received'>Not Received</MenuItem>
										<MenuItem value='changed_mind'>Changed My Mind</MenuItem>
										<MenuItem value='other'>Other</MenuItem>
									</Select>
								</FormControl>
								<TextField
									fullWidth
									multiline
									rows={3}
									label='Additional Details (optional)'
									value={refundNote}
									onChange={(e) => setRefundNote(e.target.value)}
								/>
								{refundMessage && (
									<Alert severity={refundMessage.type} sx={{ mt: 2 }}>
										{refundMessage.text}
									</Alert>
								)}
							</DialogContent>
							<DialogActions sx={{ p: 2, pt: 0 }}>
								<Button
									onClick={() => setRefundDialogOpen(false)}
									disabled={refundLoading}
									sx={{ textTransform: 'none' }}
								>
									Cancel
								</Button>
								<Button
									onClick={handleRefundSubmit}
									disabled={refundLoading || !refundReason}
									variant='contained'
									sx={{ textTransform: 'none', backgroundColor: primaryColor, '&:hover': { backgroundColor: primaryColor, filter: 'brightness(1.1)' } }}
								>
									{refundLoading ? <CircularProgress size={20} color='inherit' /> : 'Submit Request'}
								</Button>
							</DialogActions>
						</Dialog>

						{/* Clear Cache Button */}
						<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
							<Button
								onClick={handleClearCache}
								sx={{
									textTransform: 'none',
									color: theme.palette.text.secondary,
									'&:hover': {
										backgroundColor: 'transparent',
										color: theme.palette.text.primary,
									},
								}}
							>
								<Typography variant='caption'>
									Clear Cache
								</Typography>
							</Button>
						</Box>

						{/* Leave Store Confirmation Dialog */}
						<Dialog
							open={showLeaveDialog}
							onClose={() => !isLeavingStore && setShowLeaveDialog(false)}
							maxWidth='sm'
							fullWidth
						>
							<DialogTitle>Leave {config.companyName}?</DialogTitle>
							<DialogContent>
								<Typography variant='body1' sx={{ mb: 2 }}>
									Are you sure you want to leave this store? You will no longer have access to:
								</Typography>
								<Stack spacing={1} sx={{ pl: 2 }}>
									<Typography variant='body2' color='text.secondary'>
										• This store's products and orders
									</Typography>
									<Typography variant='body2' color='text.secondary'>
										• Your account settings for this store
									</Typography>
									{hasPortalAccessForStore && (
										<Typography variant='body2' color='text.secondary'>
											• Admin portal access
										</Typography>
									)}
								</Stack>
								<Typography variant='body2' sx={{ mt: 2, fontWeight: 500 }}>
									You can rejoin this store later if needed.
								</Typography>
							</DialogContent>
							<DialogActions sx={{ p: 2, pt: 0 }}>
								<Button
									onClick={() => setShowLeaveDialog(false)}
									disabled={isLeavingStore}
									sx={{ textTransform: 'none' }}
								>
									Cancel
								</Button>
								<Button
									onClick={handleLeaveStore}
									disabled={isLeavingStore}
									variant='contained'
									color='warning'
									sx={{
										textTransform: 'none',
										fontWeight: 600
									}}
								>
									{isLeavingStore ? 'Leaving...' : 'Leave Store'}
								</Button>
							</DialogActions>
						</Dialog>
					</Container>
				</Box>
			</Box>
		);
	};

	// Main render with fade transitions
	const renderAuthContent = () => {
		if (authView === 'existingUser') {
			return <ExistingUserContent />;
		}
		if (authView === 'register') {
			return <RegisterForm />;
		}
		return <LoginForm />;
	};

	// Determine what to show:
	// - Auth form: when not authenticated, OR when transitioning (keep form visible with spinner)
	// - Profile: when authenticated AND not transitioning
	const showAuth = !authenticated || isTransitioning;

	if (showAuth) {
		return (
			<Box
				sx={{
					flex: 1,
					height: '100%',
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					backgroundColor: authBackgroundColor
				}}
			>
				{renderAuthContent()}
			</Box>
		);
	}

	// Show profile content when authenticated and not transitioning
	return (
		<Box
			sx={{
				height: '100%',
				width: '100%',
				backgroundColor: theme.palette.background.default
			}}
		>
			<ProfileContent />
		</Box>
	);
};
